import React from 'react';
import { 
  CurrencyCircleDollar, MapPin, Phone, User, 
  CalendarDots, CheckCircle, Clock, Bus, Compass
} from '@phosphor-icons/react';
import './ParentTransportSection.css';

function formatCurrency(amount) {
  return `GHS ${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ParentTransportSection({ data, childName }) {
  if (!data?.enrolled) {
    return (
      <div className="transport-not-enrolled">
        <div className="not-enrolled-content">
          <MapPin size={56} weight="thin" color="#9CA3AF" />
          <h3>Not Enrolled in Transport</h3>
          <p>{childName} is not currently enrolled in the transport service</p>
        </div>
      </div>
    );
  }

  const totalFees = data.recent_fees?.reduce((sum, fee) => sum + fee.amount_due, 0) || 0;
  const totalPaid = data.recent_fees?.reduce((sum, fee) => sum + fee.amount_paid, 0) || 0;
  const totalBalance = totalFees - totalPaid;
  const allFeesPaid = data.recent_fees?.every(fee => fee.is_paid) ?? false;

  return (
    <div className="transport-section-container">
      {/* Header */}
      <div className="transport-section-header">
        <div className="header-content">
          <h2>Transport Service</h2>
          <p>Manage transport route and fees for {childName}</p>
        </div>
        <div className={`header-badge ${allFeesPaid ? 'paid' : 'pending'}`}>
          {allFeesPaid ? (
            <>
              <CheckCircle size={20} weight="fill" />
              All Fees Paid
            </>
          ) : (
            <>
              <Clock size={20} weight="fill" />
              Pending Payment
            </>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="transport-stats-grid">
        <div className="stat-card route-info">
          <div className="stat-icon">
            <Compass size={28} weight="duotone" color="#3B82F6" />
          </div>
          <div className="stat-body">
            <span className="stat-label">Route Name</span>
            <span className="stat-value">{data.route_name}</span>
            <span className="stat-detail">Assigned route</span>
          </div>
        </div>

        <div className="stat-card enrollment-info">
          <div className="stat-icon">
            <CalendarDots size={28} weight="duotone" color="#059669" />
          </div>
          <div className="stat-body">
            <span className="stat-label">Enrollment Date</span>
            <span className="stat-value">{new Date(data.enrollment_date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            <span className="stat-detail">Service started</span>
          </div>
        </div>

        <div className={`stat-card fee-balance ${totalBalance > 0 ? 'warning' : 'success'}`}>
          <div className="stat-icon">
            <CurrencyCircleDollar size={28} weight="duotone" color={totalBalance > 0 ? '#DC2626' : '#059669'} />
          </div>
          <div className="stat-body">
            <span className="stat-label">Fee Balance</span>
            <span className="stat-value">{formatCurrency(totalBalance)}</span>
            <span className="stat-detail">{totalBalance > 0 ? 'Outstanding' : 'Fully Paid'}</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="transport-content-grid">
        {/* Left Column - Route Details */}
        <div className="transport-details-card">
          <div className="card-header">
            <h3>
              <Compass size={22} weight="duotone" />
              Route Details
            </h3>
          </div>
          <div className="card-body">
            <div className="detail-group">
              <label className="detail-label">Route Name</label>
              <div className="detail-value">{data.route_name || 'Not Specified'}</div>
            </div>
            <div className="detail-group">
              <label className="detail-label">Pickup Point</label>
              <div className="detail-value">{data.pickup_point || 'Not Specified'}</div>
            </div>
            <div className="detail-group">
              <label className="detail-label">Dropoff Point</label>
              <div className="detail-value">{data.dropoff_point || 'Not Specified'}</div>
            </div>
            <div className="detail-group">
              <label className="detail-label">Enrollment Date</label>
              <div className="detail-value">{new Date(data.enrollment_date).toLocaleDateString('en-GB', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
            </div>
          </div>
        </div>

        {/* Right Column - Contact Information */}
        <div className="transport-contact-card">
          <div className="card-header">
            <h3>
              <Phone size={22} weight="duotone" />
              Emergency Contact
            </h3>
          </div>
          <div className="card-body">
            {data.emergency_contact ? (
              <div className="contact-item">
                <div className="contact-icon">
                  <User size={18} weight="duotone" />
                </div>
                <div className="contact-info">
                  <span className="contact-role">Contact Person</span>
                  <span className="contact-value">{data.emergency_contact}</span>
                </div>
              </div>
            ) : (
              <div className="contact-item empty-contact">
                <span>No emergency contact provided</span>
              </div>
            )}
            {data.emergency_contact_phone ? (
              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={18} weight="duotone" color="#3B82F6" />
                </div>
                <div className="contact-info">
                  <span className="contact-role">Phone Number</span>
                  <span className="contact-value">{data.emergency_contact_phone}</span>
                </div>
              </div>
            ) : (
              <div className="contact-item empty-contact">
                <span>No phone number provided</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fees Section */}
      <div className="transport-fees-section">
        <div className="fees-header">
          <h3>
            <CurrencyCircleDollar size={22} weight="duotone" />
            Transport Fee Summary
          </h3>
          <div className="fee-summary-badges">
            <div className="badge-item">
              <span className="badge-label">Total Due:</span>
              <span className="badge-value">{formatCurrency(totalFees)}</span>
            </div>
            <div className="badge-item">
              <span className="badge-label">Total Paid:</span>
              <span className="badge-value paid">{formatCurrency(totalPaid)}</span>
            </div>
            <div className="badge-item">
              <span className="badge-label">Balance:</span>
              <span className={`badge-value ${totalBalance > 0 ? 'warning' : 'success'}`}>{formatCurrency(totalBalance)}</span>
            </div>
          </div>
        </div>

        {data.recent_fees && data.recent_fees.length > 0 ? (
          <div className="fees-items">
            {data.recent_fees.map(fee => (
              <div key={fee.id} className={`fee-item ${fee.is_paid ? 'paid' : 'pending'}`}>
                <div className="fee-item-left">
                  <div className={`fee-status-dot ${fee.is_paid ? 'paid' : 'pending'}`}></div>
                  <div className="fee-item-info">
                    <span className="fee-type">{fee.fee_type}</span>
                    <span className="fee-period">
                      {fee.due_date ? `Due: ${new Date(fee.due_date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Due date not specified'}
                    </span>
                  </div>
                </div>
                <div className="fee-item-amounts">
                  <div className="amount-cell">
                    <span className="amount-label">Due</span>
                    <span className="amount-value">{formatCurrency(fee.amount_due)}</span>
                  </div>
                  <div className="amount-cell">
                    <span className="amount-label">Paid</span>
                    <span className="amount-value paid">{formatCurrency(fee.amount_paid)}</span>
                  </div>
                  <div className="amount-cell">
                    <span className="amount-label">Balance</span>
                    <span className={`amount-value ${fee.balance > 0 ? 'warning' : 'success'}`}>
                      {formatCurrency(fee.balance)}
                    </span>
                  </div>
                </div>
                <div className="fee-item-status">
                  <span className={`status-badge ${fee.is_paid ? 'paid' : 'pending'}`}>
                    {fee.is_paid ? <><CheckCircle size={14} weight="fill" /> Paid</> : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-fees-state">
            <CurrencyCircleDollar size={40} weight="light" color="#D1D5DB" />
            <p>No fees recorded for transport service</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParentTransportSection;
